// crewbit-admin/src/lib/redact.ts
//
// Canonical redactor for log-line sanitization. Mirrored byte-for-byte across:
//   - crewbit-admin/src/lib/redact.ts  (this file — TESTED here via vitest)
//   - crewbit-mobile/lib/redact.ts     (RN parallel — keep diff = 0 modulo header)
//   - src/lib/redact.js                (web parallel — same shape, .js for the
//                                       JS-only Vite root)
//
// Tests live at `redact.test.ts` (admin only — vitest is set up here). The
// test suite is the contract: any new redaction case adds a regex AND a test;
// any code change to this file runs against the existing suite. Mobile + web
// inherit the patterns by mirror, not by import — keeping the dependency
// graph between the three runtimes flat.
//
// Pattern philosophy: target the *value* portion of known-secret-shaped
// substrings while preserving the key name so the redacted log still tells
// you what kind of secret was scrubbed. JWTs are caught by their structural
// shape (eyJ...eyJ...sig) which works regardless of how they were embedded.
//
// New patterns: ADD A TEST FIRST in `redact.test.ts`, watch it fail, then
// add the regex below.

/**
 * Replace known-secret-shaped substrings with a `<redacted>` placeholder
 * while preserving enough surrounding context to keep the log diagnostic.
 *
 * Idempotent: redactString(redactString(x)) === redactString(x).
 *
 * @param s arbitrary string. May be empty. Never throws.
 */
export function redactString(s: string): string {
  if (!s) return s;
  let out = s;

  // ── Token-like query/fragment params: `key=value` shapes ──
  // Stop on `&`, whitespace, quote, `#`, or end-of-string.
  out = out.replace(
    /\b(access_token|refresh_token|apikey|api_key|provider_token|id_token)=([^&\s"'#]+)/gi,
    '$1=<redacted>',
  );

  // ── Authorization: Bearer <token> ──
  out = out.replace(
    /(authorization\s*[:=]\s*bearer\s+)([^\s"']+)/gi,
    '$1<redacted>',
  );

  // ── Stripe secret keys (sk_live_ / sk_test_ / rk_live_ / rk_test_) ──
  // Allows underscores per Stripe's actual key shape.
  out = out.replace(
    /\b((?:sk|rk)_(?:live|test))_[A-Za-z0-9_]{6,}/g,
    '$1_<redacted>',
  );

  // ── JWT triple-segment: eyJ<base64url>.eyJ<base64url>.<base64url> ──
  // Catches Supabase service keys, anon keys, session tokens, ID tokens,
  // and arbitrary JWTs from anywhere. Required `eyJ` prefix on header +
  // payload makes false positives very unlikely (`eyJ` decodes to `{"` so
  // it's specifically a JSON-header signature).
  out = out.replace(
    /eyJ[A-Za-z0-9_-]{8,}\.eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g,
    '<jwt:redacted>',
  );

  return out;
}

/**
 * Redact secret-shaped content from any value. Strings get `redactString`;
 * Errors get `error.message` redacted via a fresh Error (preserving stack);
 * objects get round-tripped through JSON serialization with the redactor
 * applied to the serialized form. Other types pass through unchanged.
 *
 * Returns a NEW value when redaction occurred — does NOT mutate the input.
 *
 * For non-serializable inputs (cyclic refs, functions, Symbols), the JSON
 * round-trip throws or silently drops — fall through and return the input
 * as-is. Caller's responsibility to avoid logging gnarly objects directly.
 */
export function redactValue(arg: unknown): unknown {
  if (typeof arg === 'string') return redactString(arg);
  if (arg instanceof Error) {
    const next = new Error(redactString(arg.message));
    next.name = arg.name;
    next.stack = arg.stack ? redactString(arg.stack) : undefined;
    return next;
  }
  if (arg && typeof arg === 'object') {
    try {
      return JSON.parse(redactString(JSON.stringify(arg)));
    } catch {
      return arg;
    }
  }
  return arg;
}
